import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { RotateCcw, Brain } from "lucide-react";

const GCS_CRITERIA = [
  {
    id: "eye",
    label: "Eye Opening",
    letter: "E",
    scores: [
      { value: 4, desc: "Spontaneous" },
      { value: 3, desc: "To voice / command" },
      { value: 2, desc: "To pressure / pain" },
      { value: 1, desc: "No response" },
    ],
  },
  {
    id: "verbal",
    label: "Verbal Response",
    letter: "V",
    scores: [
      { value: 5, desc: "Oriented" },
      { value: 4, desc: "Confused" },
      { value: 3, desc: "Inappropriate words" },
      { value: 2, desc: "Incomprehensible sounds" },
      { value: 1, desc: "No response" },
    ],
  },
  {
    id: "motor",
    label: "Motor Response",
    letter: "M",
    scores: [
      { value: 6, desc: "Obeys commands" },
      { value: 5, desc: "Localizes pain" },
      { value: 4, desc: "Withdrawal from pain" },
      { value: 3, desc: "Abnormal flexion (decorticate)" },
      { value: 2, desc: "Extension (decerebrate)" },
      { value: 1, desc: "No response" },
    ],
  },
];

const PEDIATRIC_VERBAL = [
  { value: 5, desc: "Coos, babbles (age-appropriate)" },
  { value: 4, desc: "Irritable, crying" },
  { value: 3, desc: "Cries to pain" },
  { value: 2, desc: "Moans to pain" },
  { value: 1, desc: "No response" },
];

function getInterpretation(score, answered) {
  if (answered < 3) return { label: "Incomplete", color: "bg-zinc-500", textColor: "text-muted-foreground", bgColor: "bg-muted/30", guidance: "Score all 3 components" };
  if (score >= 13) return { label: "Mild", color: "bg-green-500", textColor: "text-green-500", bgColor: "bg-green-500/10 border-green-500/30", guidance: "GCS 13-15 — Minor brain injury" };
  if (score >= 9) return { label: "Moderate", color: "bg-amber-500", textColor: "text-amber-500", bgColor: "bg-amber-500/10 border-amber-500/30", guidance: "GCS 9-12 — Moderate brain injury" };
  return { label: "Severe", color: "bg-red-500", textColor: "text-red-500", bgColor: "bg-red-500/10 border-red-500/30", guidance: "GCS 3-8 — Severe brain injury. Consider intubation." };
}

export default function GCSCalculator() {
  const [scores, setScores] = useState({});
  const [open, setOpen] = useState(false);
  const [pediatricMode, setPediatricMode] = useState(false);

  const answered = Object.keys(scores).length;
  const total = answered === 3
    ? (scores.eye || 0) + (scores.verbal || 0) + (scores.motor || 0)
    : Object.values(scores).reduce((s, v) => s + v, 0);
  const interpretation = getInterpretation(total, answered);

  const handleSelect = (criterionId, value) => {
    setScores(prev => {
      if (prev[criterionId] === value) {
        const next = { ...prev };
        delete next[criterionId];
        return next;
      }
      return { ...prev, [criterionId]: value };
    });
  };

  const handleReset = () => setScores({});

  const togglePediatric = () => {
    setPediatricMode(prev => !prev);
    setScores(prev => {
      const next = { ...prev };
      delete next.verbal;
      return next;
    });
  };

  const activeCriteria = GCS_CRITERIA.map(c => {
    if (c.id === "verbal" && pediatricMode) {
      return { ...c, label: "Verbal Response (Pediatric)", scores: PEDIATRIC_VERBAL };
    }
    return c;
  });

  const scoreBreakdown = answered > 0
    ? `E${scores.eye || '?'}V${scores.verbal || '?'}M${scores.motor || '?'}`
    : null;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="gap-1.5" data-testid="gcs-trigger">
          <Brain className="w-3.5 h-3.5" />
          GCS
          {answered === 3 && (
            <Badge className={`${interpretation.color} text-white ml-0.5 text-[10px] px-1.5`}>{total}</Badge>
          )}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md max-h-[90vh] overflow-auto" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle className="font-heading text-lg flex items-center gap-2">
            <Brain className="w-5 h-5" /> Glasgow Coma Scale
          </DialogTitle>
          <p className="text-sm text-muted-foreground">Tap a score for each component. Tap again to deselect.</p>
        </DialogHeader>

        {/* Pediatric toggle + Reset */}
        <div className="flex items-center justify-between">
          <Button
            variant={pediatricMode ? "default" : "outline"}
            size="sm"
            className="text-xs gap-1.5"
            onClick={togglePediatric}
            data-testid="gcs-pediatric-toggle"
          >
            {pediatricMode ? "Pediatric Verbal" : "Adult"}
          </Button>
          <Button variant="ghost" size="sm" onClick={handleReset} className="text-xs gap-1" data-testid="gcs-reset">
            <RotateCcw className="w-3 h-3" /> Reset
          </Button>
        </div>

        {/* Score Summary */}
        <div className={`rounded-lg border p-4 ${interpretation.bgColor}`} data-testid="gcs-score-summary">
          <div className="flex items-center justify-between">
            <div>
              <span className="text-xs font-medium uppercase tracking-wide">GCS Score</span>
              <div className="flex items-baseline gap-1.5">
                <span className={`text-3xl font-bold font-mono ${interpretation.textColor}`}>{total}</span>
                <span className="text-sm text-muted-foreground">/ 15</span>
              </div>
              {scoreBreakdown && (
                <span className="text-xs font-mono text-muted-foreground" data-testid="gcs-breakdown">{scoreBreakdown}</span>
              )}
            </div>
            <Badge className={`${interpretation.color} text-white`}>{interpretation.label}</Badge>
          </div>
          {answered === 3 && (
            <div className="mt-2 pt-2 border-t border-current/10">
              <p className={`font-semibold text-sm ${interpretation.textColor}`} data-testid="gcs-guidance">{interpretation.guidance}</p>
            </div>
          )}
        </div>

        {/* GCS Criteria */}
        <div className="space-y-4">
          {activeCriteria.map((criterion) => (
            <div key={criterion.id} data-testid={`gcs-item-${criterion.id}`}>
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-sm font-medium">
                  <span className="text-primary font-bold">{criterion.letter}</span> — {criterion.label}
                </span>
                {scores[criterion.id] !== undefined && (
                  <Badge variant="outline" className="font-mono text-xs">{scores[criterion.id]}</Badge>
                )}
              </div>
              <div className="space-y-1">
                {criterion.scores.map(({ value, desc }) => (
                  <button
                    key={value}
                    onClick={() => handleSelect(criterion.id, value)}
                    className={`w-full text-left rounded-md px-3 py-2 text-sm flex items-center gap-3 ${
                      scores[criterion.id] === value
                        ? value >= 4 ? 'bg-green-500/20 text-green-400 ring-1 ring-green-500/50'
                        : value >= 2 ? 'bg-amber-500/20 text-amber-400 ring-1 ring-amber-500/50'
                        : 'bg-red-500/20 text-red-400 ring-1 ring-red-500/50'
                        : 'bg-secondary/30 hover:bg-secondary/60 text-foreground'
                    }`}
                    data-testid={`gcs-${criterion.id}-${value}`}
                  >
                    <span className="font-bold font-mono w-5 text-center shrink-0">{value}</span>
                    <span>{desc}</span>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
